import {useState} from 'react'
import {useQuery} from 'react-query'
import {fetchStudentAttendanceReport} from './api'
import {PageTitle} from '../../../_metronic/layout/core'
import CRUDTable from '../../../components/table/CRUDTable'
import toast, {Toaster} from 'react-hot-toast'
import viewProps from '../viewProps'
import {Error500} from '../../modules/errors/components/Error500'
import TopBarProgress from 'react-topbar-progress-indicator'
import {Processing} from '../../../components/Loaders'
import {Helmet} from 'react-helmet'
import Select from 'react-select'
import {fetchTripNumberList} from '../tripNumber/api'
import {fetchShiftList} from '../shift/api'
import {mapFieldBasedOnCondition} from '../../../helpers/mapFieldBasedOnCondition'
import {StudentAttendanceReportPayload} from './type'
import ExportToExcelButton from './ExportToExcelButton'
import {differenceInDays, isSameMonth, parseISO} from 'date-fns'
import {fetchSubSchoolDetails} from '../profile/api'

const StudentAttendanceReport = () => {
  const [filters, setFilters] = useState<StudentAttendanceReportPayload>({
    trip_type: 'all',
    shift_id: 'all',
    trip_number: '',
    from_date: '',
    to_date: '',
    regID: 'all',
    sub_school_id: 'all'
  })

  const {data: shift, isLoading: isLoadingShifts} = useQuery('shift', fetchShiftList, {
    refetchOnWindowFocus: false,
  })
  const {data: subschools, isLoading: isLoadingschool} = useQuery('sub_school', fetchSubSchoolDetails, {
    refetchOnWindowFocus: false,
  })
  const {data: tripNumbers, isLoading: isLoadingTripNums} = useQuery(
    'trip-numbers',
    fetchTripNumberList,
    {
      refetchOnWindowFocus: false,
    }
  )
  const {
    data: reportData,
    isLoading: isLoadingReport,
    isFetching,
    isError,
    refetch,
  } = useQuery(
    'student-attendance-report',
    () =>
      fetchStudentAttendanceReport({
        trip_type: filters.trip_type ?? '',
        shift_id: filters.shift_id ?? '',
        trip_number: filters.trip_number ?? '',
        regID: filters.regID ?? '',
        from_date: filters.from_date ?? '',
        to_date: filters.to_date ?? '',
        sub_school_id: filters.sub_school_id ?? '',
      }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
    }
  )

  const handleApplyFilters = () => {
    const {from_date, to_date, trip_number, shift_id, trip_type, sub_school_id} = filters

    if (!trip_number || !shift_id || !trip_type || !from_date || !to_date || !sub_school_id) {
      toast.error('Please select all filters')
      return
    }

    const fromDate = parseISO(from_date)
    const toDate = parseISO(to_date)

    if (!isSameMonth(fromDate, toDate)) {
      toast.error('Date range must be within the same calendar month.')
      return
    }

    const daysDiff = differenceInDays(toDate, fromDate)

    if (daysDiff < 0) {
      toast.error('From Date must be before or equal to To Date.')
      return
    }

    if (daysDiff > 31) {
      toast.error('Date range should not exceed 31 days.')
      return
    }

    toast.loading('Applying filters...')
    refetch()
      .then(() => {
        toast.dismiss()
        toast.success('Filters applied successfully.')
      })
      .catch(() => {
        toast.dismiss()
        toast.error('Failed to fetch inspection report.')
      })
  }

  const cellModifier = {
    conductor_date: ({value}: {value: string}) => {
      return value
        ? new Date(value).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          })
        : ''
    },
    driver_date: ({value}: {value: string}) => {
      return value
        ? new Date(value).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          })
        : ''
    },
    security_date: ({value}: {value: string}) => {
      return value
        ? new Date(value).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          })
        : ''
    },
    team_leader_date: ({value}: {value: string}) => {
      return value
        ? new Date(value).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
          })
        : ''
    },
    
  }
  if (isLoadingShifts || isLoadingTripNums || isLoadingschool) {
    return <Processing />
  }

  const tripNumberOptions = [
  ...tripNumbers!.data!.map((item: any) => ({
    label: item.trip_number.toUpperCase(),
    value: item.trip_number,
  })),
]
  const shiftOptions = [{shift_name: 'all', shift_id: 'all'}, ...shift?.data!].map((item: any) => ({
    label: item.shift_name.toUpperCase(),
    value: item.shift_id,
  }))
  const subschoolOptions = [{sub_school_name: 'all', sub_school_id: 'all'}, ...subschools?.data!].map((item: any) => ({
    label: item.sub_school_name.toUpperCase(),
    value: item.sub_school_id,
  }))

  const dateFields = ['conductor_date', 'driver_date', 'security_date', 'team_leader_date']

  let updatedReport = reportData?.data
  if (updatedReport) {
    dateFields.forEach((field) => {
      updatedReport = mapFieldBasedOnCondition(updatedReport, field, field, (value: any) =>
        value
          ? new Date(value).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            })
          : ''
      )
    })    
  }
   let updateddata = reportData?.data
  if(updateddata)
  {
  updateddata = updateddata.map((r: any) => {
      const checkin = r.checkin_time 
      const checkout = r.checkout_time
      const tripType = r.daily_bus_trip_type ?? ''

      // If both empty, leave as-is (so it doesn't overwrite with parentheses)
      if (!checkin && !checkout) {
        return {...r, daily_bus_trip_type: tripType}
      }

      // Use the raw values as-is (no formatting) and put placeholders when missing
      const checkinVal = checkin || '--'
      const checkoutVal = checkout || '--'
      return {
        ...r,
        daily_bus_trip_type: `${tripType} - (${checkinVal} - ${checkoutVal})`,
      }
    })
  }
  const selectedFromYear = new Date(filters.from_date).getFullYear()
  const selectedToYear = new Date(filters.to_date).getFullYear()
  const selectedMonth = new Date(filters.from_date).toLocaleString('default', {month: 'long'})

  const academicYear =
    selectedFromYear === selectedToYear
      ? `${selectedFromYear}`
      : `${selectedFromYear} - ${selectedToYear}`

  return (
    <>
      <Helmet>
        <title>TrackNAB | Student Attendance Report</title>
      </Helmet>
      <PageTitle>Student Attendance Report</PageTitle>

      {/* Filters Section */}
      <div className="mb-4 p-4 border rounded shadow-sm bg-light">
        <div className="row g-3">
          <div className="col-md-2">
            <label className="form-label">From Date</label>
            <input
              type="date"
              className="form-control"
              value={filters.from_date}
              onChange={(e) => setFilters({...filters, from_date: e.target.value})}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">To Date</label>
            <input
              type="date"
              className="form-control"
              value={filters.to_date}
              onChange={(e) => setFilters({...filters, to_date: e.target.value})}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Trip Type</label>
            <select
              className="form-select"
              value={filters.trip_type}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  trip_type: e.target.value as 'pickup' | 'drop',
                })
              }
            >
              <option value="all">All</option>
              <option value="pickup">Pickup</option>
              <option value="drop">Drop</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Trip Number</label>
            <Select
              options={tripNumberOptions}
              onChange={(opt: any) => setFilters({...filters, trip_number: opt?.value || null})}
              value={tripNumberOptions.find((opt) => opt.value === filters.trip_number) || null}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Shift</label>
            <Select
              options={shiftOptions}
              onChange={(opt: any) => setFilters({...filters, shift_id: opt?.value || null})}
              value={shiftOptions.find((opt) => opt.value === filters.shift_id) || null}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Student Sub School</label>
            <Select
              options={subschoolOptions}
              onChange={(opt: any) => setFilters({...filters, sub_school_id: opt?.value || null})}
              value={subschoolOptions.find((opt) => opt.value === filters.sub_school_id) || null}
            />
          </div>
          <div className="col-md-12 d-flex justify-content-end">
            <button className="btn btn-primary mt-2" onClick={handleApplyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      {isLoadingReport && <Processing />}
      {isError && <Error500 />}
      {reportData?.data?.length! > 0 ? (
        <CRUDTable
          data={updateddata}
          {...viewProps.StudentAttendanceReport}
          fieldToLabelMap={viewProps.StudentAttendanceReport.primaryFieldsToLabelMap}
          cellModifier={cellModifier}
          headerExtras={
            <>
              {/* <div className="fs-3">{`Mes School Security Student Attendance Report - ${selectedShiftName} - ${selectedTripType} - Year`}</div> */}
              <div className="ms-auto">
                <ExportToExcelButton
                  data={updatedReport!}
                  fileName="Student Attendance Report"
                  heading={`ACADEMIC YEAR ${academicYear} BUS ATTENDANCE REGISTER MONTH - ${selectedMonth.toUpperCase()}`}
                  dateRange={[filters.from_date, filters.to_date]}
                />
              </div>
            </>
          }
          // isSearchable={false}
        />
      ) : (
        <div className="fs-2 p-6 text-center">Apply filters / No data for applied filters</div>
      )}

      {isFetching && <TopBarProgress />}
      <Toaster />
    </>
  )
}

export default StudentAttendanceReport
